import React, { useEffect, useMemo } from "react";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  ColorModeProvider,
  useColorMode,
  COLOR_MODE_STORAGE_KEY,
} from "../contexts/ColorModeContext";
import { createAppTheme } from "../theme/createAppTheme";

function ThemedApp({ children }: { children: React.ReactNode }) {
  const { mode } = useColorMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", mode === "dark");
    root.style.colorScheme = mode;
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
    root.style.setProperty(
      "--sami-background",
      mode === "dark" ? "#0f1117" : "#f5f7fa",
    );
    root.style.setProperty(
      "--sami-surface",
      mode === "dark" ? "#1a1d26" : "#ffffff",
    );
    root.style.setProperty("--sami-nav-surface", root.style.getPropertyValue("--sami-background"));
  }, [mode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

/**
 * Wraps the app with MUI theme + CssBaseline and Emotion `injectFirst` so
 * Tailwind utility classes can override component styles when needed.
 */
export function MuiTailwindProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ColorModeProvider>
      <ThemedApp>{children}</ThemedApp>
    </ColorModeProvider>
  );
}
